import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import { Formik } from 'formik'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { _, withCapture } from 'utils'
import { useAsync } from 'hooks'
import FullPageLoading from 'components/FullPageLoading'
import { getNodes } from 'graphql/utils'
import { ShowType } from 'generated/graphql'
import FormTabs from '../forms/FormTabs'
import GeneralSetting from './tabs/GeneralSetting'
import SpeakerInfo from './tabs/SpeakerInfo'
import VirtualEvent from './tabs/VirtualEvent'
import { updateShow } from './graphql/mutation'
import { ShowValidation } from './CreateShow'
import { getShow } from './graphql/query'
import { showNotification, showErrorNotification } from '../notifications/notificationsSlice'
import { getChangedValues } from '../forms/utils'
import App from './tabs/App'

const transformDataForForm = (
    show: ShowType
) => show && ({
    ...show,
    startDate: new Date(show.startDate),
    endDate: new Date(show.endDate),
    releaseDate: show.releaseDate ? new Date(show.releaseDate) : new Date(),
    // For old descriptions created by old portal,
    // the new line is <br>\n, but with quill, it transforms new line to </p><p>
    // need this replacer to correct the unsaved statement of form.
    description: show.description.replace(/<br>\n/g, '</p><p>'),
    performerIds: getNodes(show.performers).map(({ id: performerId }) => performerId),
    virtualSpaceId: show.virtualSpace?.id,
    moderatorEmail: show.moderator?.email || show.moderator?.firstName,
    tags: show.tags.map((e) => e.id),
    onlyForSegments: show.onlyForSegments.map((e) => e.id),
    containerId: show.container?.id,
    sponsorId: show.sponsor?.id,
    mapitemId: show.mapitem?.id
})

const UpdateShow = () => {
    const { id } = useParams<{ id: string }>()
    const getShowsAsync = useCallback(
        () => getShow(+id),
        [id]
    )

    const [isUploading, setIsUploading] = useState(false)
    const { value, status, execute: callGetShow } = useAsync(getShowsAsync)
    const [initialValues, setInitialValues] = useState()
    const dispatch = useDispatch()

    useEffect(() => {
        // @ts-ignore
        setInitialValues(transformDataForForm(value))
    }, [value])

    const SpeakerMemo = useMemo(() => () => (
        <SpeakerInfo
            name="performerIds"
            performers={getNodes(value?.performers)}
        />
    ), [value])

    if (!initialValues) {
        return null
    }

    return (status === 'success' && value) ? (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={withCapture(async (values, { resetForm }) => {
                setIsUploading(true)
                const submitValues = getChangedValues(initialValues, values)
                const response = await updateShow(+id, submitValues)
                if (response.data) {
                    const newValues = {
                        ...values,
                        virtualImage: response.data.updateShows[0].virtualImage,
                        image: response.data.updateShows[0].image,
                        attachment: response.data.updateShows[0].attachment
                    }
                    resetForm({ values: newValues })
                    setInitialValues(newValues)
                    if (submitValues.performerIds || submitValues.moderatorEmail) {
                        // When updating performerIds, re-call getShow to get new data of performers.
                        callGetShow()
                    }
                    dispatch(showNotification({
                        type: 'success',
                        body: _('form.notification.update_success'),
                    }))
                } else {
                    dispatch(showErrorNotification(response.errors))
                }
                setIsUploading(false)
            })}
            validationSchema={ShowValidation}
        >
            <>
                <FormTabs
                    header={_('show.create_header')}
                    tabs={[
                        { id: 'general', name: _('show.tabs.general_setting'), Component: GeneralSetting },
                        {
                            id: 'speaker',
                            name: _('show.tabs.speaker_info'),
                            Component: SpeakerMemo
                        },
                        { id: 'virtualspace', name: _('show.tabs.virtual_event'), Component: VirtualEvent },
                        { id: 'app', name: _('show.tabs.app'), Component: App }
                    ]}
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    ) : (
        <FullPageLoading />
    )
}

export default UpdateShow
